<template>
  <b-row class="m-3">
    <b-col md="8" offset-md="2" lg="6" offset-lg="3">
      <!-- Success state -->
      <b-card v-if="emailSent" header="Password Reset Email Sent" header-bg-variant="success" header-text-variant="white" bg-variant="light">
        <p class="mb-3">
          A password reset link has been sent to <strong>{{ sentEmail }}</strong
          >. Please check your inbox and follow the instructions.
        </p>
        <p class="mb-3" style="font-size:0.9rem;">
          If you do not receive the email within the next <strong>15 minutes</strong>, check your Junk/Spam folder or
          <b-link @click="resetForm">try again</b-link>.
        </p>
        <div class="text-right">
          <b-button size="sm" variant="primary" :to="{ name: 'login' }">Back to Login</b-button>
        </div>
      </b-card>

      <!-- Request form -->
      <b-card v-else header="Forgot Password" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <p class="mb-3">
          If the information entered matches our records, we will email you a one-time login and instructions to reset your password.
        </p>
        <b-form @submit.prevent="onSubmit">
          <b-form-group label="UBC ID:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.ubcid" v-mask="'U-####-####'" :state="validated('ubcid')" placeholder="e.g. U-1234-5678" trim @paste="onUbcIdPaste" />
            <b-form-invalid-feedback>A valid UBC ID is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Email:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.email" type="email" :state="validated('email')" placeholder="Email address on file" trim />
            <b-form-invalid-feedback>A valid email address is required</b-form-invalid-feedback>
          </b-form-group>

          <div class="d-flex justify-content-between align-items-center mt-3">
            <b-button size="sm" variant="secondary" :disabled="loading" :to="{ name: 'login' }">Cancel</b-button>
            <b-button size="sm" type="submit" variant="success" :disabled="loading || submitDisabled">
              <b-spinner v-if="loading" small class="mr-1" />
              Send Reset Email
            </b-button>
          </div>
        </b-form>

        <hr class="my-3" />
        <div class="text-center">
          <small class="text-muted">Don't have access to your email or don't know your username?</small><br />
          <b-button size="sm" variant="outline-secondary" class="mt-2" :to="{ name: 'alternate-account-lookup' }">
            Alternate Account Lookup
          </b-button>
        </div>
      </b-card>

      <recaptcha-v2-modal
        v-model="showV2Challenge"
        :site-key="recaptchaV2SiteKey"
        @resolved="onV2Resolved"
        @cancelled="onV2Cancelled"
        @error="onV2Error"
      />
    </b-col>
  </b-row>
</template>
<script>
import { Vue, Component, Mixins } from 'vue-property-decorator';
import memberDao from '@/dao/member_dao';
import { emailValidator } from '@/util/validator';
import { isValid as isValidUbcId } from '@/util/ubc_id';
import RecaptchaV2Modal from '@/components/RecaptchaV2Modal.vue';
import recaptchaMixin from '@/mixins/recaptcha_fallback';
import { renderErrorBody } from '@/util/error';

@Component({
  metaInfo: {
    title: 'Member Portal - Forgot Password'
  },
  components: { RecaptchaV2Modal }
})
export default class RequestPassword extends Mixins(recaptchaMixin) {
  form = { ubcid: '', email: '' };
  loading = false;
  submitted = false;
  emailSent = false;
  sentEmail = '';

  get submitDisabled() {
    return ['ubcid', 'email'].some(k => !this.validated(k));
  }

  validated(field) {
    if (!this.submitted && !this.form[field]) return undefined;
    const value = (this.form[field] || '').trim();
    switch (field) {
      case 'ubcid':
        return isValidUbcId(value);
      case 'email':
        return emailValidator(value);
      default:
        return true;
    }
  }

  onUbcIdPaste(e) {
    e.preventDefault();
    const raw = (e.clipboardData || window.clipboardData).getData('text');
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let val = 'U';
    if (digits.length > 0) val += '-' + digits.slice(0, 4);
    if (digits.length > 4) val += '-' + digits.slice(4);
    this.form.ubcid = val;
  }

  mounted() {
    this.loadRecaptcha();
  }

  resetForm() {
    this.emailSent = false;
    this.sentEmail = '';
    this.submitted = false;
    this.form = { ubcid: '', email: '' };
  }

  async onSubmit() {
    try {
      this.submitted = true;
      if (this.submitDisabled) {
        return this.$bvToast.toast('Please correct the highlighted errors.', { title: 'Error', solid: true, variant: 'danger' });
      }
      this.loading = true;

      await this.submitWithRecaptcha('request_password', tokens =>
        memberDao.requestPassword({
          ubcid: this.form.ubcid.trim(),
          email: this.form.email.trim(),
          ...tokens
        })
      );
      this.sentEmail = this.form.email.trim();
      this.emailSent = true;
    } catch (error) {
      const body = renderErrorBody(this.$createElement, (error && error.message) || 'An unknown error occurred, please try again.');
      this.$bvModal.msgBoxOk(body, {
        title: 'Unable to Send Reset Email',
        okTitle: 'OK',
        okVariant: 'danger',
        centered: true,
        headerBgVariant: 'danger',
        headerTextVariant: 'white'
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>
<style scoped></style>
